/**
 * Centralized athlete-profile module readiness.
 *
 * Every optional module on a public athlete profile asks this file whether it is
 * allowed to render. A module is only ready when its data is present, valid,
 * published/approved and visible to the current viewer. Nothing here is
 * athlete-specific and nothing here writes to the database.
 */

import { getSupportConfig, type SupportableAthlete } from "@/lib/support";
import { toEmbedUrl, isDirectVideo } from "@/lib/video";
import type { QAItem, BookingCTA } from "@/components/athlete/ProfileFieldEditors";

export type ModuleKey =
  | "about"
  | "heroQuote"
  | "stats"
  | "qa"
  | "quickFacts"
  | "photos"
  | "videos"
  | "causes"
  | "socialLinks"
  | "directSupport"
  | "publicSupporters"
  | "brandRelationships"
  | "partnershipInquiry"
  | "relatedAthletes"
  | "mantra"
  | "bookingCta";

export type ModuleState = {
  key: ModuleKey;
  label: string;
  active: boolean;
  /** Human readable explanation shown only in the admin diagnostic panel. */
  reason: string;
  /** Fields that would have to be supplied to activate the module. */
  missing: string[];
};

const state = (
  key: ModuleKey,
  label: string,
  active: boolean,
  reason: string,
  missing: string[] = [],
): ModuleState => ({ key, label, active, reason, missing: active ? [] : missing });

/* ---------------------------------------------------------------- helpers */

export const text = (value?: string | null): string | null => {
  const v = (value ?? "").replace(/\u00a0/g, " ").trim();
  return v ? v : null;
};

/** Strips markup so whitespace-only rich text never activates a module. */
export const richText = (value?: string | null): string | null => {
  if (!value) return null;
  const plain = value
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  return plain ? value : null;
};

/** Markup-free version of a rich-text value, for truncated summaries. */
export const plainText = (value?: string | null): string | null => {
  if (!value) return null;
  const v = value.replace(/<[^>]*>/g, " ").replace(/&nbsp;/g, " ").replace(/&amp;/g, "&").replace(/\s+/g, " ").trim();
  return v ? v : null;
};

/** Only http(s) destinations are considered safe for public link-outs. */
export const safeHttpUrl = (value?: string | null): string | null => {
  const v = text(value);
  if (!v) return null;
  try {
    const u = new URL(v);
    return u.protocol === "http:" || u.protocol === "https:" ? u.toString() : null;
  } catch {
    return null;
  }
};

export const safeEmail = (value?: string | null): string | null => {
  const v = text(value);
  return v && /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(v) ? v : null;
};

/** A CTA destination may be an http(s) link or a mailto address. */
export const safeCtaHref = (value?: string | null): string | null => {
  const v = text(value);
  if (!v) return null;
  if (v.toLowerCase().startsWith("mailto:")) return safeEmail(v.slice(7)) ? v : null;
  return safeHttpUrl(v);
};

export const isSupportedVideo = (url?: string | null): boolean => {
  const v = text(url);
  if (!v || !safeHttpUrl(v)) return false;
  return Boolean(toEmbedUrl(v)) || isDirectVideo(v);
};

/* ------------------------------------------------------------------ types */

export type ProfileAthlete = SupportableAthlete & {
  id: string;
  name: string;
  sport: string | null;
  tagline?: string | null;
  position_short?: string | null;
  position_long?: string | null;
  stats?: { label: string; value: string }[] | null;
  bio: string | null;
  gallery_paths: string[] | null;
  video_urls: string[] | null;
  qa: QAItem[] | null;
  booking_ctas: BookingCTA[] | null;
  booking_inquiry_email: string | null;
  mantra_quote: string | null;
  height: string | null;
  hometown: string | null;
  college: string | null;
  occupation: string | null;
  years_playing: number | null;
  throws_hand: string | null;
  high_school: string | null;
  instagram_url: string | null;
  tiktok_url: string | null;
  facebook_url: string | null;
  youtube_url: string | null;
  linkedin_url: string | null;
  custom_links: { label: string; url: string }[] | null;
  partnership_interests?: string | null;
};

export type ProfileRelations = {
  /** Already filtered to published rows by the page query. */
  causes: { id: string; name: string; blurb: string | null; donate_url: string | null }[];
  products: { id: string; brand_name: string; product_name: string; image_path: string | null }[];
  related: { id: string }[];
  /** Public supporter rows the viewer is actually allowed to read. */
  publicSupporters: { id: string }[];
  /** False while the supporter query is still in flight. */
  supportersLoaded: boolean;
};

/* -------------------------------------------------------------- readiness */

export const hasAbout = (a: ProfileAthlete): ModuleState =>
  state("about", "About", Boolean(richText(a.bio)), richText(a.bio) ? "Bio present" : "Bio empty or whitespace only", ["bio"]);

export const hasQA = (a: ProfileAthlete): ModuleState => {
  const items = validQA(a);
  return state("qa", "Q&A / Get to Know", items.length > 0, items.length ? `${items.length} complete Q&A entries` : "No Q&A entry has both a question and an answer", ["qa"]);
};

export const validQA = (a: ProfileAthlete): QAItem[] =>
  (a.qa ?? []).filter((q) => q && text(q.question) && text(q.answer));

/** The single position value shown in the hero: short form first, long as fallback. */
export const heroPosition = (a: ProfileAthlete): string | null =>
  text(a.position_short) ?? text(a.position_long) ?? null;

/** The fullest position wording available — never printed twice with the hero value. */
export const canonicalPosition = (a: ProfileAthlete): string | null =>
  text(a.position_long) ?? text(a.position_short) ?? null;

export const quickFactList = (a: ProfileAthlete) => {
  /**
   * Jersey number, team and sport live in the hero and are deliberately excluded
   * here. Position is included only when the canonical (long) wording adds
   * something the hero abbreviation does not already say.
   */
  const hero = heroPosition(a);
  const canonical = canonicalPosition(a);
  const positionFact =
    canonical && canonical.toLowerCase() !== (hero ?? "").toLowerCase() ? canonical : null;
  return [
    { label: "Position", value: positionFact },
    { label: "Height", value: text(a.height) },
    { label: "Hometown", value: text(a.hometown) },
    { label: "College", value: text(a.college) },
    { label: "Occupation", value: text(a.occupation) },
    { label: "Years Playing", value: a.years_playing != null && a.years_playing > 0 ? String(a.years_playing) : null },
    { label: "Throws", value: text(a.throws_hand) },
    { label: "High School", value: text(a.high_school) },
  ].filter((f): f is { label: string; value: string } => Boolean(f.value));
};

export const hasQuickFacts = (a: ProfileAthlete): ModuleState => {
  const facts = quickFactList(a);
  return state("quickFacts", "Quick Facts", facts.length > 0, facts.length ? `${facts.length} facts beyond the hero` : "No facts beyond jersey, position, team and sport", [
    "height", "hometown", "college", "occupation", "years_playing", "throws_hand", "high_school",
  ]);
};

export const validPhotos = (a: ProfileAthlete, resolve: (p: string) => string | null): string[] =>
  (a.gallery_paths ?? []).map((p) => (text(p) ? resolve(p) : null)).filter((u): u is string => Boolean(u));

export const hasPhotos = (a: ProfileAthlete, resolve: (p: string) => string | null): ModuleState => {
  const photos = validPhotos(a, resolve);
  return state("photos", "Photos", photos.length > 0, photos.length ? `${photos.length} resolvable images` : "No gallery image resolves to a public URL", ["gallery_paths"]);
};

export const validVideos = (a: ProfileAthlete): string[] =>
  (a.video_urls ?? []).map((v) => text(v)).filter((v): v is string => Boolean(v) && isSupportedVideo(v));

export const hasVideos = (a: ProfileAthlete): ModuleState => {
  const videos = validVideos(a);
  return state("videos", "Videos", videos.length > 0, videos.length ? `${videos.length} supported video URLs` : "No supported, valid video URL", ["video_urls"]);
};

export const validCauses = (r: ProfileRelations) =>
  r.causes.filter((c) => text(c.name) && (safeHttpUrl(c.donate_url) || text(c.blurb) || text(c.id)));

export const hasCauses = (r: ProfileRelations): ModuleState => {
  const causes = validCauses(r);
  return state("causes", "Causes", causes.length > 0, causes.length ? `${causes.length} published causes` : "No published cause with a name and a destination", ["causes"]);
};

export const socialLinkList = (a: ProfileAthlete) => ({
  instagram_url: safeHttpUrl(a.instagram_url),
  tiktok_url: safeHttpUrl(a.tiktok_url),
  facebook_url: safeHttpUrl(a.facebook_url),
  youtube_url: safeHttpUrl(a.youtube_url),
  linkedin_url: safeHttpUrl(a.linkedin_url),
  custom_links: (a.custom_links ?? [])
    .filter((l) => l && text(l.label) && safeHttpUrl(l.url))
    .map((l) => ({ label: text(l.label)!, url: safeHttpUrl(l.url)! })),
});

export const socialLinkCount = (a: ProfileAthlete) => {
  const l = socialLinkList(a);
  return [l.instagram_url, l.tiktok_url, l.facebook_url, l.youtube_url, l.linkedin_url].filter(Boolean).length + l.custom_links.length;
};

export const hasSocialLinks = (a: ProfileAthlete): ModuleState => {
  const count = socialLinkCount(a);
  return state("socialLinks", "Connect", count > 0, count ? `${count} valid links` : "No social or custom link has a valid http(s) URL", [
    "instagram_url", "tiktok_url", "facebook_url", "youtube_url", "linkedin_url", "custom_links",
  ]);
};

export const hasDirectSupport = (a: ProfileAthlete): ModuleState => {
  // Preserves the existing getSupportConfig contract — this only adds URL safety
  // and the disclosure-copy requirement on top of it.
  const config = getSupportConfig(a);
  const url = safeHttpUrl(config.paymentUrl);
  const provider = text(config.provider);
  const disclosure = Boolean(text(a.support_blurb) || true); // default disclosure copy always exists
  const active = config.enabled && Boolean(url) && Boolean(provider) && disclosure;
  const missing: string[] = [];
  if (!a.support_enabled) missing.push("support_enabled");
  if (!url) missing.push("payment_url (valid https)");
  if (!provider) missing.push("payment_provider");
  return state(
    "directSupport",
    "Direct Support",
    active,
    active ? `Enabled via ${provider}` : missing.length ? `Blocked by: ${missing.join(", ")}` : "Support not enabled",
    missing,
  );
};

export const hasPublicSupporters = (a: ProfileAthlete, r: ProfileRelations): ModuleState => {
  const supportReady = hasDirectSupport(a).active;
  const count = r.publicSupporters.length;
  const active = supportReady && r.supportersLoaded && count > 0;
  const missing: string[] = [];
  if (!supportReady) missing.push("direct support must be enabled first");
  if (supportReady && r.supportersLoaded && count === 0) missing.push("no supporter has opted to appear publicly");
  if (!r.supportersLoaded) missing.push("supporter list still loading");
  return state("publicSupporters", "Public Supporter Wall", active, active ? `${count} consented supporters` : missing.join(", "), missing);
};

export const validProducts = (r: ProfileRelations) =>
  r.products.filter((p) => text(p.brand_name) && text(p.product_name));

export const hasBrandRelationships = (r: ProfileRelations): ModuleState => {
  const products = validProducts(r);
  return state("brandRelationships", "Brand Relationships", products.length > 0, products.length ? `${products.length} approved products` : "No approved, published brand relationship", ["brand_products"]);
};

export const bookingCtaList = (a: ProfileAthlete) =>
  (a.booking_ctas ?? [])
    .filter((c) => c && text(c.label) && safeCtaHref(c.url))
    .map((c) => ({ label: text(c.label)!, url: safeCtaHref(c.url)! }));

export const primaryCtaHref = (a: ProfileAthlete): string | null => {
  const ctas = bookingCtaList(a);
  if (ctas[0]) return ctas[0].url;
  const email = safeEmail(a.booking_inquiry_email);
  return email ? `mailto:${email}` : null;
};

export const hasBookingCta = (a: ProfileAthlete): ModuleState => {
  const count = bookingCtaList(a).length;
  const href = primaryCtaHref(a);
  const active = count > 0 || Boolean(href);
  return state("bookingCta", "Work-with CTA", active, active ? `${count} CTA links` : "No valid booking link or inquiry email", ["booking_ctas", "booking_inquiry_email"]);
};

export const hasPartnershipInquiry = (a: ProfileAthlete): ModuleState => {
  /**
   * Commercial module: needs an explicit signal that the athlete is open for
   * business — a stated partnership interest, a valid inquiry email, or at least
   * one valid booking CTA. Without one of those the form stays off the page.
   */
  const interests = text(a.partnership_interests);
  const email = safeEmail(a.booking_inquiry_email);
  const ctas = bookingCtaList(a).length;
  const active = Boolean(interests || email || ctas > 0);
  const missing = active ? [] : ["partnership_interests", "booking_inquiry_email", "booking_ctas"];
  return state(
    "partnershipInquiry",
    "Partnership Inquiries",
    active,
    active
      ? `Open via ${[interests && "stated interests", email && "inquiry email", ctas > 0 && "booking CTA"].filter(Boolean).join(", ")}`
      : "Athlete has not signalled any partnership opportunity",
    missing,
  );
};

const sameCopy = (a?: string | null, b?: string | null) =>
  Boolean(a && b && a.trim().toLowerCase() === b.trim().toLowerCase());

/** Mantra never repeats the hero quote (tagline). */
export const hasMantra = (a: ProfileAthlete): ModuleState => {
  const mantra = text(a.mantra_quote);
  const duplicate = sameCopy(mantra, text(a.tagline));
  const active = Boolean(mantra) && !duplicate;
  return state(
    "mantra",
    "Mantra",
    active,
    active ? "Mantra present" : duplicate ? "Mantra is identical to the hero quote" : "No mantra text",
    ["mantra_quote"],
  );
};

/** Tagline, shown as the hero quote directly beneath the hero actions. */
export const hasHeroQuote = (a: ProfileAthlete): ModuleState =>
  state("heroQuote", "Hero Quote", Boolean(text(a.tagline)), text(a.tagline) ? "Tagline present" : "No tagline", ["tagline"]);

export const validStats = (a: ProfileAthlete) =>
  (a.stats ?? [])
    .filter((s) => s && text(s.label) && text(s.value))
    .map((s) => ({ label: text(s.label)!, value: text(s.value)! }));

export const hasStats = (a: ProfileAthlete): ModuleState => {
  const stats = validStats(a);
  return state("stats", "By the Numbers", stats.length > 0, stats.length ? `${stats.length} stats` : "No stat has both a label and a value", ["stats"]);
};

export const hasRelatedAthletes = (r: ProfileRelations): ModuleState =>
  state("relatedAthletes", "Related Athletes", r.related.length > 0, r.related.length ? `${r.related.length} related published athletes` : "No other published athlete on this team or sport", ["athlete_teams", "sport"]);

/* --------------------------------------------------------- module summary */

export type ProfileModules = Record<ModuleKey, ModuleState>;

export const buildProfileModules = (
  a: ProfileAthlete,
  r: ProfileRelations,
  resolve: (p: string) => string | null,
): ProfileModules => ({
  about: hasAbout(a),
  heroQuote: hasHeroQuote(a),
  stats: hasStats(a),
  qa: hasQA(a),
  quickFacts: hasQuickFacts(a),
  photos: hasPhotos(a, resolve),
  videos: hasVideos(a),
  causes: hasCauses(r),
  socialLinks: hasSocialLinks(a),
  directSupport: hasDirectSupport(a),
  publicSupporters: hasPublicSupporters(a, r),
  brandRelationships: hasBrandRelationships(r),
  partnershipInquiry: hasPartnershipInquiry(a),
  relatedAthletes: hasRelatedAthletes(r),
  mantra: hasMantra(a),
  bookingCta: hasBookingCta(a),
});

/** Rail-eligible modules — used to decide whether a desktop rail exists at all. */
export const RAIL_MODULES: ModuleKey[] = ["quickFacts", "socialLinks", "bookingCta", "relatedAthletes"];

export const activeRailCount = (m: ProfileModules) => RAIL_MODULES.filter((k) => m[k].active).length;

/** Modules that give the main column enough substance to sit beside a rail. */
const MAIN_MODULES: ModuleKey[] = [
  "about",
  "mantra",
  "stats",
  "qa",
  "photos",
  "videos",
  "causes",
  "brandRelationships",
  "directSupport",
  "partnershipInquiry",
];

export const activeMainCount = (m: ProfileModules) => MAIN_MODULES.filter((k) => m[k].active).length;

/**
 * Rough rendered weight of the rail, derived from the amount of content each
 * rail module actually holds — never from measured pixel heights. Two tiny
 * cards (e.g. Connect + Related) score too low to justify a 320px gutter, so
 * they fall back into the main flow instead of stranding empty space.
 */
export const railWeight = (
  m: ProfileModules,
  counts: { facts: number; links: number; ctas: number; related: number; mantraChars?: number },
): number => {
  let w = 0;
  if (m.quickFacts.active) w += counts.facts >= 4 ? 3 : 2;
  if (m.socialLinks.active) w += counts.links >= 3 ? 2 : 1;
  if (m.bookingCta.active) w += counts.ctas >= 2 ? 3 : 2;
  if (m.relatedAthletes.active) w += counts.related >= 3 ? 2 : 1;
  return w;
};

/**
 * A desktop rail is only worth rendering when the rail itself carries real
 * weight AND the main column has at least two substantial modules beside it.
 */
export const shouldRenderRail = (
  m: ProfileModules,
  counts?: { facts: number; links: number; ctas: number; related: number; mantraChars?: number },
): boolean => {
  if (!counts) return activeRailCount(m) >= 2 && activeMainCount(m) >= 2;
  return railWeight(m, counts) >= 5 && activeMainCount(m) >= 2;
};


/* ------------------------------------------- admin preview simulation only */

/**
 * Everything below powers the admin-only Athlete Profile Preview tool.
 * It is pure client state: it overlays clearly labelled placeholder values on a
 * local copy of the athlete row before readiness is computed, and it never
 * writes to (or reads extra rows from) the database.
 */

export const PREVIEW_LABEL = "Preview placeholder — not saved";

export type SimulationValue = "on" | "off";
export type PreviewSimulation = Partial<Record<ModuleKey, SimulationValue>>;

/** Optional modules an admin may toggle in preview. */
export const SIMULATABLE_MODULES: ModuleKey[] = [
  "about",
  "heroQuote",
  "stats",
  "qa",
  "quickFacts",
  "mantra",
  "socialLinks",
  "photos",
  "videos",
  "causes",
  "brandRelationships",
  "bookingCta",
  "partnershipInquiry",
  "directSupport",
  "publicSupporters",
];

export const MODULE_KIND: Record<ModuleKey, "content" | "commercial" | "derived"> = {
  about: "content",
  heroQuote: "content",
  stats: "content",
  qa: "content",
  quickFacts: "content",
  photos: "content",
  videos: "content",
  causes: "content",
  socialLinks: "content",
  mantra: "content",
  bookingCta: "content",
  brandRelationships: "content",
  directSupport: "commercial",
  publicSupporters: "commercial",
  partnershipInquiry: "commercial",
  relatedAthletes: "derived",
};

/** Modules gated by an explicit stored on/off setting. */
export const MODULE_ENABLE_FLAG: Partial<Record<ModuleKey, string>> = {
  directSupport: "support_enabled",
  publicSupporters: "support_enabled + supporter consent",
};

export const MODULE_ADVICE: Record<ModuleKey, string> = {
  about: "Ask the athlete to add her story in the dashboard bio editor.",
  heroQuote: "Add a short tagline — it appears as the quote beneath the hero actions.",
  stats: "Add at least one stat with both a label and a value.",
  qa: "Add at least one Q&A entry with both a question and an answer.",
  quickFacts: "Fill in height, hometown, college, occupation, years playing, throws or high school.",
  photos: "Upload gallery images that resolve to a public URL.",
  videos: "Add a YouTube, Vimeo or direct video URL.",
  causes: "Link a published cause with a name and a destination.",
  socialLinks: "Add at least one valid https social or custom link.",
  mantra: "Add a short mantra quote.",
  bookingCta: "Add a booking CTA link or a valid inquiry email.",
  brandRelationships: "Link an approved, published brand product to this athlete.",
  directSupport: "Enable support and connect a valid provider payment link in Admin → Support.",
  publicSupporters: "Wall appears once direct support is live and a supporter consents to be shown.",
  partnershipInquiry: "Add partnership interests, an inquiry email or a booking CTA.",
  relatedAthletes: "Publish other athletes on the same team or sport.",
};

export const parseSimulation = (raw: string | null): PreviewSimulation => {
  const sim: PreviewSimulation = {};
  if (!raw) return sim;
  raw.split(",").forEach((entry) => {
    const [key, value] = entry.split(":");
    if ((SIMULATABLE_MODULES as string[]).includes(key) && (value === "on" || value === "off")) {
      sim[key as ModuleKey] = value;
    }
  });
  return sim;
};

export const serializeSimulation = (sim: PreviewSimulation): string =>
  Object.entries(sim)
    .filter(([, v]) => v === "on" || v === "off")
    .map(([k, v]) => `${k}:${v}`)
    .join(",");

const PREVIEW_PHOTO = "/og-image.png";

/**
 * Returns local copies of the athlete row and its relations with the requested
 * modules forced present (clearly labelled placeholders) or absent. Inputs are
 * never mutated and nothing is persisted.
 */
export const applyPreviewSimulation = <A extends ProfileAthlete, R extends ProfileRelations>(
  athlete: A,
  relations: R,
  sim: PreviewSimulation,
): { athlete: A; relations: R } => {
  if (!Object.keys(sim).length) return { athlete, relations };
  const a: A = { ...athlete };
  const r: R = { ...relations };
  const on = (k: ModuleKey) => sim[k] === "on";
  const off = (k: ModuleKey) => sim[k] === "off";

  if (on("about")) a.bio = `<p>${PREVIEW_LABEL}. This is simulated story copy used only to check layout.</p>`;
  if (off("about")) a.bio = null;

  if (on("heroQuote")) a.tagline = `${PREVIEW_LABEL} — simulated hero quote.`;
  if (off("heroQuote")) a.tagline = null;

  if (on("stats"))
    a.stats = [
      { label: "Preview stat", value: "12" },
      { label: "Preview stat", value: "3.4" },
    ] as unknown as A["stats"];
  if (off("stats")) a.stats = [] as unknown as A["stats"];

  if (on("qa"))
    a.qa = [
      { question: `${PREVIEW_LABEL} — question one?`, answer: "Simulated answer used only for layout testing." },
      { question: `${PREVIEW_LABEL} — question two?`, answer: "Simulated answer used only for layout testing." },
    ] as unknown as A["qa"];
  if (off("qa")) a.qa = [] as unknown as A["qa"];

  if (on("quickFacts")) {
    a.height = "5'8\" (preview)";
    a.hometown = `${PREVIEW_LABEL}`;
    a.college = "Preview University";
    a.occupation = "Preview placeholder";
  }
  if (off("quickFacts")) {
    a.height = null; a.hometown = null; a.college = null; a.occupation = null;
    a.years_playing = null; a.throws_hand = null; a.high_school = null;
  }

  if (on("mantra")) a.mantra_quote = `${PREVIEW_LABEL} — simulated mantra for layout testing.`;
  if (off("mantra")) a.mantra_quote = null;

  if (on("socialLinks")) {
    a.instagram_url = "https://example.com/preview-placeholder";
    a.custom_links = [{ label: `${PREVIEW_LABEL}`, url: "https://example.com/preview-placeholder" }];
  }
  if (off("socialLinks")) {
    a.instagram_url = null; a.tiktok_url = null; a.facebook_url = null;
    a.youtube_url = null; a.linkedin_url = null; a.custom_links = [];
  }

  if (on("photos")) a.gallery_paths = [PREVIEW_PHOTO, PREVIEW_PHOTO, PREVIEW_PHOTO];
  if (off("photos")) a.gallery_paths = [];

  if (on("videos")) a.video_urls = ["https://www.youtube.com/watch?v=aqz-KE-bpKQ"];
  if (off("videos")) a.video_urls = [];

  if (on("causes"))
    r.causes = [
      { id: "preview-cause", name: `${PREVIEW_LABEL} cause`, blurb: "Simulated cause copy for layout testing.", donate_url: "https://example.com/preview-placeholder" },
    ] as unknown as R["causes"];
  if (off("causes")) r.causes = [] as R["causes"];

  if (on("brandRelationships"))
    r.products = [
      { id: "preview-product-1", brand_name: `${PREVIEW_LABEL}`, product_name: "Simulated product", image_path: null },
      { id: "preview-product-2", brand_name: `${PREVIEW_LABEL}`, product_name: "Simulated product", image_path: null },
    ] as unknown as R["products"];
  if (off("brandRelationships")) r.products = [] as R["products"];

  if (on("bookingCta")) a.booking_ctas = [{ label: `${PREVIEW_LABEL} CTA`, url: "https://example.com/preview-placeholder" }] as unknown as A["booking_ctas"];
  if (off("bookingCta")) { a.booking_ctas = [] as unknown as A["booking_ctas"]; a.booking_inquiry_email = null; }

  if (on("partnershipInquiry")) a.partnership_interests = `${PREVIEW_LABEL} — appearances, speaking, content.`;
  if (off("partnershipInquiry")) {
    a.partnership_interests = null;
    a.booking_inquiry_email = null;
    a.booking_ctas = [] as unknown as A["booking_ctas"];
  }

  if (on("directSupport") || on("publicSupporters")) {
    a.support_enabled = true;
    a.payment_provider = a.payment_provider || "kofi";
    a.payment_url = "https://example.com/preview-placeholder";
    a.support_blurb = `${PREVIEW_LABEL} — simulated support message.`;
  }
  if (off("directSupport")) a.support_enabled = false;

  if (on("publicSupporters")) {
    r.supportersLoaded = true;
    r.publicSupporters = [
      { id: "preview-supporter-1", supporter_name: PREVIEW_LABEL, message: "Simulated supporter note.", transaction_timestamp: new Date().toISOString() },
      { id: "preview-supporter-2", supporter_name: PREVIEW_LABEL, message: null, transaction_timestamp: new Date().toISOString() },
    ] as unknown as R["publicSupporters"];
  }
  if (off("publicSupporters")) r.publicSupporters = [] as R["publicSupporters"];

  return { athlete: a, relations: r };
};

/** Simulation presets used by the "no optional modules" / "everything on" checks. */
export const simulationPreset = (kind: "none" | "all"): PreviewSimulation =>
  Object.fromEntries(SIMULATABLE_MODULES.map((k) => [k, kind === "all" ? "on" : "off"])) as PreviewSimulation;
